import { useEffect, useRef } from 'react'

const STORAGE_KEY = 'app_version'

interface VersionCheckOptions {
  onVersionChanged?: (newVersion: string, oldVersion: string) => void
}

export const useVersionCheck = ({ onVersionChanged }: VersionCheckOptions = {}) => {
  const callbackRef = useRef(onVersionChanged)

  useEffect(() => {
    callbackRef.current = onVersionChanged
  }, [onVersionChanged])

  // Check version and notify if changed
  const checkVersion = (newVersion: string) => {
    const storedVersion = sessionStorage.getItem(STORAGE_KEY)

    if (storedVersion && storedVersion !== newVersion) {
      // Version changed - notify with old version before updating
      callbackRef.current?.(newVersion, storedVersion)
      sessionStorage.setItem(STORAGE_KEY, newVersion)
    } else if (!storedVersion) {
      // First time - just store it
      sessionStorage.setItem(STORAGE_KEY, newVersion)
    }
  }

  // Initialize version on mount by fetching from /version.json
  useEffect(() => {
    fetchVersion()
  }, [])

  // Handle app focus detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // User returned to app - fetch version to check if changed
        fetchVersion()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  // Deprecated: handleAuthResponse is no longer used since we fetch version directly from /version.json
  const handleAuthResponse = (apiVersion: string | undefined) => {
    // No-op: API version and PWA version are separate
  }

  // Fetch version from the version endpoint
  const fetchVersion = async () => {
    try {
      const response = await fetch('/version.json', {
        cache: 'no-store'
      })

      if (!response.ok) return

      const { version } = await response.json()
      checkVersion(version)
    } catch (error) {
      console.warn('Failed to check app version:', error)
    }
  }

  return { checkVersion, handleAuthResponse, fetchVersion }
}
